import {
  AfterViewInit,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ResetPasswordEmailDto } from '@core/interfaces/email.interface';
import { EmailService } from '@core/services/email.service';
import { FormUtils } from '@core/utils/form-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-request-reset-password',
  templateUrl: './request-reset-password.component.html',
  styleUrls: ['./request-reset-password.component.scss'],
  standalone: true,
  imports: [TranslateModule, FormsModule],
  host: { ngSkipHydration: 'true' },
})
@UntilDestroy()
export class RequestResetPasswordComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input() prefillEmail: string = '';
  // New: allow parent to pass redirect path that will be appended to the reset URL
  @Input() redirectPath: string = '';

  resetPassword: ResetPasswordEmailDto = {
    email: '',
    languageCode: 'it',
    redirectPath: undefined,
  };
  private subscription: Subscription;
  isEmailInvalid = false;
  emailSent = false;
  platformService = inject(PlatformService);
  private languageService = inject(LanguageService);
  private emailService = inject(EmailService);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private router = inject(Router);

  ngOnChanges(changes: SimpleChanges): void {
    const c = changes['prefillEmail'];
    if (c) {
      const next = (c.currentValue ?? '').trim();
      const prev = (c.previousValue ?? '').trim();
      // Only set from prefill when it's provided and when the user hasn't typed a different value yet
      if (
        next &&
        (!this.resetPassword.email || this.resetPassword.email === prev)
      ) {
        this.resetPassword.email = next;
      }
    }
    const rp = changes['redirectPath'];
    if (rp) {
      this.resetPassword.redirectPath = (rp.currentValue ?? '').toString();
    }
  }

  ngOnInit(): void {
    if (this.prefillEmail) {
      this.resetPassword.email = this.prefillEmail;
    }
    // Always compute current redirect from router URL; ensures accuracy even if parent didn't pass it
    const current = this.computeCurrentRedirectPath();
    if (current) {
      this.resetPassword.redirectPath = current;
    } else if (this.redirectPath) {
      // Fallback to any provided input
      this.resetPassword.redirectPath = this.redirectPath;
    }
  }

  ngAfterViewInit() {
    this.platformService.runOnBrowser(() => {
      this.emailService.showReCaptchaBadge();
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.platformService.runOnBrowser(() => {
      this.emailService.hideReCaptchaBadge();
    });
  }

  onSubmit() {
    if (FormUtils.emailRegex.test(this.resetPassword.email)) {
      this.isEmailInvalid = false;
      this.resetPassword.languageCode =
        this.languageService.userSelectedLanguage.code;
      // Refresh redirectPath from the current URL right before sending
      const current = this.computeCurrentRedirectPath();
      if (current) {
        this.resetPassword.redirectPath = current;
      }
      // Keep redirectPath as provided; backend will encode and include as query param
      this.subscription = this.recaptchaV3Service
        .execute('resetPassword')
        .subscribe((token: string) => {
          this.emailService
            .verifyReCaptcha({ responseToken: token })
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              this.emailService
                .sendResetPasswordEmail(this.resetPassword)
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                  this.emailSent = true;
                });
            });
        });
    } else {
      this.isEmailInvalid = true;
    }
  }

  private computeCurrentRedirectPath(): string {
    const currentUrl = this.router.url || '';
    if (!currentUrl) return '';

    // Parse URL for robust extraction
    const tree = this.router.parseUrl(currentUrl);
    const primary = tree.root.children['primary'];
    const segments = primary?.segments ?? [];
    let path = segments.map((s) => s.path).join('/');

    // Strip language prefix (e.g., it/, en/)
    const lang = (this.languageService.userSelectedLanguage?.code || '').toLowerCase();
    if (lang && (path === lang || path.startsWith(lang + '/'))) {
      path = path.substring(lang.length + (path.length > lang.length ? 1 : 0));
    }

    // Rebuild query string
    const qp = (tree.queryParams || {}) as Record<string, string | number | boolean>;
    const usp = new URLSearchParams();
    Object.keys(qp).forEach((k) => {
      const v = qp[k];
      if (v !== undefined && v !== null) {
        usp.set(k, String(v));
      }
    });
    const queryStr = usp.toString();
    const fragment = tree.fragment ? `#${tree.fragment}` : '';
    const rebuilt = queryStr ? `${path}?${queryStr}${fragment}` : `${path}${fragment}`;
    return rebuilt || 'home';
  }
}
